import React from 'react';
import type { PrintColumn, SimplePrintLine } from '@/print-engine';
import { buildSimplePrintDocument } from '@/print-engine/adapters/simple-document';
import { buildCommercialA4PrintDocument } from '@/print-engine/adapters/commercial-a4-adapter';
import { LINE_HEIGHT_PX } from '@/print-engine/page-template';
import { getCompanyLetterhead } from '@/print-engine/company-letterhead';
import { formatPrintDate } from '@/utils/purchaseOrderPrint';

function money(v: unknown): string {
  const n = Number(v ?? 0);
  return (Number.isNaN(n) ? 0 : n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function str(v: unknown, fallback = ''): string {
  if (v == null || v === '') return fallback;
  return String(v);
}

function pct(v: unknown): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return str(v);
  return `${n}%`;
}

function yn(v: unknown): string {
  return v === true || v === 1 || v === '1' ? 'Yes' : v === false || v === 0 || v === '0' ? 'No' : '';
}

function agreementLines(a: Record<string, unknown>, override?: Record<string, unknown>[]) {
  if (override?.length) return override;
  return Array.isArray(a.lines) ? (a.lines as Record<string, unknown>[]) : [];
}

export function buildSubcontractAgreementPrintModel(input: {
  agreement: Record<string, unknown>;
  lines?: Record<string, unknown>[];
  supplierName?: string;
  projectLabel?: string;
}) {
  const a = input.agreement;
  const currency = str(a.currency, 'AED');
  const status = str(a.status, 'Draft');
  const isOfficial = status === 'Active' || status === 'Approved';
  const lines = agreementLines(a, input.lines);
  const supplierName = input.supplierName || str(a.supplier_name) || str(a.supplierName);
  const projectLabel =
    input.projectLabel ||
    str(a.project_label) ||
    str(a.project_number
      ? `${a.project_number}${a.project_name ? ` — ${a.project_name}` : ''}`
      : a.project_name) ||
    str(a.project_id);
  const agreementNo = str(a.agreement_number, 'SCA');
  const title = str(a.title) || 'Subcontractor Agreement';
  const createdAt = formatPrintDate(str(a.created_at) || null) || '';

  const columns: PrintColumn[] = [
    { key: 'sno', label: '#' },
    { key: 'code', label: 'Code' },
    { key: 'description', label: 'Description' },
    { key: 'qty', label: 'Qty', align: 'right' },
    { key: 'rate', label: 'Unit rate', align: 'right' },
    { key: 'amount', label: 'Amount', align: 'right' },
    { key: 'category', label: 'Category' },
  ];

  const sorted = [...lines].sort(
    (x, y) => Number(x.line_number ?? x.lineNumber ?? 0) - Number(y.line_number ?? y.lineNumber ?? 0)
  );

  const printLines: SimplePrintLine[] = sorted.length
    ? sorted.map((ln, i) => {
        const code = str(ln.line_code || ln.lineCode);
        const description = str(ln.description, code || `Line ${i + 1}`);
        const qty = ln.qty != null ? String(ln.qty) : '';
        const rate =
          ln.unit_rate != null || ln.unitRate != null
            ? money(ln.unit_rate ?? ln.unitRate)
            : '';
        const amount = money(ln.amount);
        const category = str(ln.cost_category || ln.costCategory, 'Subcontract');
        return {
          lineNumber: Number(ln.line_number ?? ln.lineNumber ?? i + 1),
          description,
          cells: {
            sno: String(i + 1),
            code,
            qty,
            rate,
            amount,
            category,
          },
        };
      })
    : [
        {
          lineNumber: 1,
          description: title,
          cells: {
            sno: '1',
            code: '',
            qty: '1',
            rate: money(a.contract_sum),
            amount: money(a.contract_sum),
            category: str(a.cost_category, 'Subcontract'),
          },
        },
      ];

  const leftRows: Array<{ label: string; value: string }> = [
    { label: 'M/S', value: supplierName || '—' },
    { label: 'Subject', value: title },
  ];
  if (projectLabel) leftRows.push({ label: 'Project', value: projectLabel });

  const rightRows: Array<{ label: string; value: string }> = [
    { label: 'Agreement No.', value: agreementNo },
    { label: 'Status', value: status },
    { label: 'Currency', value: currency },
  ];
  if (createdAt) rightRows.push({ label: 'Date', value: createdAt });
  if (a.pricing_basis) rightRows.push({ label: 'Pricing basis', value: str(a.pricing_basis) });
  if (a.clause_profile || a.clauseProfile) {
    rightRows.push({
      label: 'Clause profile',
      value: str(a.clause_profile || a.clauseProfile).replace(/_/g, ' '),
    });
  }

  const termRows: Array<{ label: string; value: string }> = [];
  if (a.advance_pct != null && a.advance_pct !== '') {
    termRows.push({ label: 'Advance', value: pct(a.advance_pct) });
  }
  if (a.retention_pct != null && a.retention_pct !== '') {
    termRows.push({ label: 'Retention', value: pct(a.retention_pct) });
  }
  if (a.attendance_pct != null && a.attendance_pct !== '') {
    termRows.push({ label: 'Attendance', value: pct(a.attendance_pct) });
  }
  if (yn(a.nominated)) termRows.push({ label: 'Nominated', value: 'Yes' });
  if (yn(a.pay_when_paid)) termRows.push({ label: 'Pay when paid', value: 'Yes' });
  if (a.ld_rate_per_day != null && a.ld_rate_per_day !== '') {
    termRows.push({
      label: 'LD rate / day',
      value: `${currency} ${money(a.ld_rate_per_day)}`,
    });
  }
  if (a.ld_cap != null && a.ld_cap !== '') {
    termRows.push({ label: 'LD cap', value: `${currency} ${money(a.ld_cap)}` });
  }

  const commercialTerms = (a.commercialTerms || a.commercial_terms || {}) as Record<
    string,
    unknown
  >;
  const particulars = (a.particulars || {}) as Record<string, unknown>;
  const termsExtra: string[] = [];
  for (const [k, v] of Object.entries(commercialTerms)) {
    if (v != null && String(v).trim()) termsExtra.push(`${k}: ${String(v)}`);
  }
  for (const [k, v] of Object.entries(particulars)) {
    if (v != null && String(v).trim() && typeof v !== 'object') {
      termsExtra.push(`${k}: ${String(v)}`);
    }
  }

  const lineCountNote =
    sorted.length > 0
      ? `This agreement comprises ${sorted.length} activity line${sorted.length === 1 ? '' : 's'} as tabulated below.`
      : 'Contract sum is stated as a single commercial total pending detailed activity breakdown.';

  const totalsBlock = (
    <div>
      <p>
        <strong>Contract sum:</strong> {currency} {money(a.contract_sum)}
      </p>
      <p style={{ fontSize: 11, marginTop: 4 }}>
        Amounts are exclusive of any taxes unless otherwise stated in commercial terms.
      </p>
    </div>
  );

  return buildSimplePrintDocument({
    docType: 'subcontractor_agreement',
    title: 'SUBCONTRACTOR AGREEMENT',
    subtitle: title,
    documentNumber: agreementNo,
    partyName: supplierName || title,
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    watermark: isOfficial ? undefined : { text: 'DRAFT' },
    continuationLabel: `${agreementNo} | ${supplierName || title}`,
    partyBlock: { leftRows, rightRows },
    introGreeting: 'Dear Sir / Madam,',
    introParagraphs: [
      'Please find below the particulars of this Subcontractor Agreement, including commercial terms and the schedule of activities / priced lines forming the contract sum.',
      lineCountNote,
    ],
    columns,
    lines: printLines,
    tableTitle: 'Schedule of activities / agreement lines',
    finals: [
      { id: 'totals', heightPx: 56, keepTogetherWithNext: true },
    ],
    termsHeading: termRows.length || termsExtra.length ? 'Commercial terms' : undefined,
    termRows: termRows.length ? termRows : undefined,
    termsExtra: termsExtra.length ? termsExtra : undefined,
    termsHeightPx:
      termRows.length || termsExtra.length
        ? 40 + termRows.length * (LINE_HEIGHT_PX - 1) + termsExtra.length * LINE_HEIGHT_PX
        : undefined,
    renderPayload: {
      finalBlocks: {
        totals: totalsBlock,
      },
    },
  });
}

export function buildSubcontractCertificatePrintModel(input: {
  certificate: Record<string, unknown>;
  agreement?: Record<string, unknown>;
  supplierName?: string;
  projectLabel?: string;
}) {
  const c = input.certificate;
  const a = input.agreement || {};
  const currency = str(c.currency || a.currency, 'AED');
  const status = str(c.status, 'Draft');
  const isOfficial = status === 'Certified' || status === 'Approved';
  const certNo = str(c.certificate_number, 'SPC');
  const kind = str(c.certificate_kind || c.certificateKind, 'Interim');
  const agreementNo = str(a.agreement_number || a.agreementNumber || c.agreement_id, '');
  const agreementTitle = str(a.title || a.agreement_title, '');
  const supplierName =
    input.supplierName || str(a.supplier_name) || str(a.supplierName) || '';
  const projectLabel =
    input.projectLabel ||
    str(a.project_label) ||
    str(
      a.project_number
        ? `${a.project_number}${a.project_name ? ` — ${a.project_name}` : ''}`
        : a.project_name
    );
  const periodFrom = formatPrintDate(str(c.period_from || c.periodFrom) || null);
  const periodTo = formatPrintDate(str(c.period_to || c.periodTo) || null);
  const periodLabel =
    periodFrom || periodTo ? `${periodFrom || '…'} → ${periodTo || '…'}` : '';

  const termsRaw = c.commercial_terms_json ?? c.commercialTerms ?? c.commercial_terms;
  const terms =
    typeof termsRaw === 'string'
      ? (() => {
          try {
            return JSON.parse(termsRaw) as Record<string, unknown>;
          } catch {
            return {} as Record<string, unknown>;
          }
        })()
      : ((termsRaw && typeof termsRaw === 'object' ? termsRaw : {}) as Record<string, unknown>);

  const lines = Array.isArray(c.lines) ? (c.lines as Record<string, unknown>[]) : [];
  const sorted = [...lines].sort(
    (x, y) => Number(x.line_number ?? x.lineNumber ?? 0) - Number(y.line_number ?? y.lineNumber ?? 0)
  );

  const columns: PrintColumn[] = [
    { key: 'sno', label: '#' },
    { key: 'code', label: 'Code' },
    { key: 'description', label: 'Description' },
    { key: 'prev', label: 'Prev. certified', align: 'right' },
    { key: 'qty', label: 'Period qty', align: 'right' },
    { key: 'rate', label: 'Unit rate', align: 'right' },
    { key: 'applied', label: 'This period', align: 'right' },
    { key: 'cumulative', label: 'Cumulative', align: 'right' },
  ];

  const printLines: SimplePrintLine[] = sorted.length
    ? sorted.map((ln, i) => {
        const code = str(ln.line_code || ln.lineCode);
        const description = str(ln.description, code || `Line ${i + 1}`);
        const applied = Number(ln.applied_amount ?? ln.appliedAmount ?? 0) || 0;
        const prev = Number(ln.previous_certified_amount ?? ln.previousCertifiedAmount ?? 0) || 0;
        const metaRaw = ln.meta_json ?? ln.metaJson;
        const meta =
          typeof metaRaw === 'string'
            ? (() => {
                try {
                  return JSON.parse(metaRaw) as Record<string, unknown>;
                } catch {
                  return {};
                }
              })()
            : ((metaRaw && typeof metaRaw === 'object' ? metaRaw : {}) as Record<string, unknown>);
        const qty =
          ln.quantity != null
            ? String(ln.quantity)
            : meta.thisPeriodQty != null
              ? String(meta.thisPeriodQty)
              : '';
        const rate =
          ln.unit_rate != null || ln.unitRate != null
            ? money(ln.unit_rate ?? ln.unitRate)
            : '';
        const cumulative =
          meta.cumulativeAmount != null
            ? Number(meta.cumulativeAmount)
            : Math.round((prev + applied) * 100) / 100;
        const pctHint =
          meta.thisPeriodPct != null && Number(meta.thisPeriodPct) > 0
            ? `Period ${Number(meta.thisPeriodPct)}% of line`
            : '';
        return {
          lineNumber: Number(ln.line_number ?? ln.lineNumber ?? i + 1),
          description,
          extraLines: pctHint ? [pctHint] : undefined,
          cells: {
            sno: String(i + 1),
            code,
            prev: money(prev),
            qty,
            rate,
            applied: money(applied),
            cumulative: money(cumulative),
          },
        };
      })
    : [
        {
          lineNumber: 1,
          description: str(terms.baseGrossApplied != null ? 'Work gross (header)' : 'Gross applied'),
          cells: {
            sno: '1',
            code: '',
            prev: '',
            qty: '',
            rate: '',
            applied: money(terms.baseGrossApplied ?? c.gross_applied_amount),
            cumulative: money(c.cumulative_gross_amount ?? c.gross_applied_amount),
          },
        },
      ];

  const workGross =
    terms.baseGrossApplied != null
      ? Number(terms.baseGrossApplied)
      : sorted.reduce((s, ln) => s + (Number(ln.applied_amount ?? ln.appliedAmount) || 0), 0);

  const leftRows: Array<{ label: string; value: string }> = [
    { label: 'M/S', value: supplierName || '—' },
  ];
  if (agreementNo) {
    leftRows.push({
      label: 'Agreement',
      value: agreementTitle ? `${agreementNo} — ${agreementTitle}` : agreementNo,
    });
  }
  if (projectLabel) leftRows.push({ label: 'Project', value: projectLabel });
  if (periodLabel) leftRows.push({ label: 'Period', value: periodLabel });

  const rightRows: Array<{ label: string; value: string }> = [
    { label: 'Certificate No.', value: certNo },
    { label: 'Kind', value: kind.replace(/([a-z])([A-Z])/g, '$1 $2') },
    { label: 'Status', value: status },
    { label: 'Currency', value: currency },
  ];
  if (c.revision != null) rightRows.push({ label: 'Revision', value: str(c.revision) });
  const issued = formatPrintDate(str(c.submitted_at || c.created_at) || null);
  if (issued) rightRows.push({ label: 'Date', value: issued });

  const termRows: Array<{ label: string; value: string }> = [];
  if (terms.attendanceRatePct != null) {
    termRows.push({ label: 'Attendance rate', value: pct(terms.attendanceRatePct) });
  }
  if (terms.advanceRecoveryRatePct != null) {
    termRows.push({ label: 'Advance recovery rate', value: pct(terms.advanceRecoveryRatePct) });
  }
  if (terms.retentionRatePct != null) {
    termRows.push({ label: 'Retention rate', value: pct(terms.retentionRatePct) });
  }
  if (terms.remainingAdvanceBalance != null) {
    termRows.push({
      label: 'Remaining advance (cap)',
      value: `${currency} ${money(terms.remainingAdvanceBalance)}`,
    });
  }
  if (a.contract_sum != null || a.contractSum != null) {
    termRows.push({
      label: 'Agreement contract sum',
      value: `${currency} ${money(a.contract_sum ?? a.contractSum)}`,
    });
  }

  const lineCountNote = sorted.length
    ? `This certificate values ${sorted.length} activity line${sorted.length === 1 ? '' : 's'} for the stated period against the Subcontractor Agreement.`
    : 'This certificate is valued on a header commercial total (no activity lines recorded).';

  const ratesBlock =
    termRows.length > 0 ? (
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ccc' }}>
        <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 4 }}>
          Commercial rates &amp; reference
        </div>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <tbody>
            {termRows.map((row) => (
              <tr key={row.label}>
                <td style={{ padding: '1px 0', width: '55%' }}>{row.label}</td>
                <td style={{ padding: '1px 0', textAlign: 'right' }}>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null;

  const totalsBlock = (
    <div className="print-spc-totals">
      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td>Work gross (this period)</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(workGross)}
            </td>
          </tr>
          <tr>
            <td>Dayworks</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(c.dayworks_amount)}
            </td>
          </tr>
          <tr>
            <td>Materials on site</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(c.materials_on_site_amount)}
            </td>
          </tr>
          <tr>
            <td>Attendance</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(c.attendance_amount)}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Gross before commercial</strong>
            </td>
            <td style={{ textAlign: 'right' }}>
              <strong>
                {currency}{' '}
                {money(terms.grossBeforeCommercial ?? c.gross_applied_amount)}
              </strong>
            </td>
          </tr>
          <tr>
            <td>Less: Advance recovery</td>
            <td style={{ textAlign: 'right' }}>
              ({currency} {money(c.advance_recovery_amount)})
            </td>
          </tr>
          <tr>
            <td>Less: Retention withheld</td>
            <td style={{ textAlign: 'right' }}>
              ({currency} {money(c.retention_withheld_amount)})
            </td>
          </tr>
          <tr>
            <td>Add: Retention released</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(c.retention_released_amount)}
            </td>
          </tr>
          <tr>
            <td>Less: Set-off</td>
            <td style={{ textAlign: 'right' }}>
              ({currency} {money(c.set_off_amount)})
            </td>
          </tr>
          <tr>
            <td>Free-issue materials (excluded — not payable)</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(c.free_issue_excluded_amount)}
            </td>
          </tr>
          {c.cumulative_gross_amount != null ? (
            <tr>
              <td>Cumulative work gross (advisory)</td>
              <td style={{ textAlign: 'right' }}>
                {currency} {money(c.cumulative_gross_amount)}
              </td>
            </tr>
          ) : null}
          <tr>
            <td style={{ paddingTop: 6 }}>
              <strong>Net payable this certificate</strong>
            </td>
            <td style={{ textAlign: 'right', paddingTop: 6 }}>
              <strong>
                {currency} {money(c.net_applied_amount)}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      {ratesBlock}
      <p style={{ fontSize: 10, marginTop: 8, color: '#444' }}>
        Commercial valuation only. Certification → Draft Supplier Invoice → Finance Payment Voucher.
        Free-issue is tracked and never payable.
      </p>
    </div>
  );

  // Keep valuation + rates in one final block with a generous height so the page
  // planner never parks content under the letterhead footer artwork.
  const valuationRows =
    11 + (c.cumulative_gross_amount != null ? 1 : 0) + termRows.length;
  const totalsHeightPx = Math.max(
    280,
    28 + valuationRows * LINE_HEIGHT_PX + (termRows.length ? 28 : 0) + 48 + 24
  );

  return buildSimplePrintDocument({
    docType: 'subcontract_payment_certificate',
    title: 'SUBCONTRACT PAYMENT CERTIFICATE',
    subtitle: agreementTitle || agreementNo || undefined,
    documentNumber: certNo,
    partyName: supplierName || agreementNo || certNo,
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    watermark: isOfficial ? undefined : { text: 'DRAFT' },
    continuationLabel: `${certNo} | ${supplierName || agreementNo || 'SPC'}`,
    partyBlock: { leftRows, rightRows },
    introGreeting: 'Dear Sir / Madam,',
    introParagraphs: [
      `Please find below Payment Certificate ${certNo} (${kind.replace(/([a-z])([A-Z])/g, '$1 $2')}) setting out the valuation of works for the stated period.`,
      lineCountNote,
    ],
    columns,
    lines: printLines,
    tableTitle: 'Schedule of works valued this period',
    finals: [{ id: 'totals', heightPx: totalsHeightPx }],
    renderPayload: {
      finalBlocks: {
        totals: totalsBlock,
      },
    },
  });
}

export function buildSubcontractFinalAccountPrintModel(input: {
  finalAccount: Record<string, unknown>;
  agreement?: Record<string, unknown>;
  supplierName?: string;
}) {
  const fa = input.finalAccount;
  return buildCommercialA4PrintDocument({
    docType: 'subcontract_final_account',
    title: 'SUBCONTRACT FINAL ACCOUNT',
    documentNumber: str(fa.account_number || fa.final_account_number, 'SFA'),
    partyName: input.supplierName || '',
    letterhead: true,
    metaLines: [
      { label: 'Account', value: str(fa.account_number || fa.final_account_number) },
      { label: 'Status', value: str(fa.status) },
      {
        label: 'Agreement',
        value: str(input.agreement?.agreement_number || fa.agreement_id),
      },
      { label: 'Currency', value: str(fa.currency || input.agreement?.currency, 'AED') },
    ].filter((m) => m.value),
    columns: [
      { key: 'sno', label: '#' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', align: 'right' },
    ],
    lines: [
      {
        lineNumber: 1,
        description: 'Agreed final sum',
        cells: { sno: '1', amount: money(fa.agreed_sum ?? fa.final_sum ?? fa.contract_sum) },
      },
      {
        lineNumber: 2,
        description: 'Certified to date',
        cells: { sno: '2', amount: money(fa.certified_to_date) },
      },
      {
        lineNumber: 3,
        description: 'Retention held',
        cells: { sno: '3', amount: money(fa.retention_held) },
      },
    ],
    finals: [{ id: 'note', heightPx: 40 }],
    finalBlocks: {
      note: <p>Advisory commercial statement — Finance owns AP/GL posting.</p>,
    },
  });
}
